import { Button, Tooltip } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconLink, IconQrcode } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type MouseEvent } from "react";
import { useAuth } from "../context/useAuth";
import { affiliateApi, intakeApi, recruiterApi } from "../lib/api";
import { ApiError } from "../lib/apiClient";
import { IDENTITY_KYC_SHARE_BLOCK } from "../lib/kyc";
import type { IntakeLink, KycStatus } from "../types/api";
import { ShareIntakeLinkModal } from "./ShareIntakeLinkModal";

interface Props {
  existingLink?: IntakeLink | null;
  variant?: "light" | "filled" | "subtle" | "default";
  size?: "xs" | "sm" | "md";
  fullWidth?: boolean;
  label?: string;
  onClick?: (event: MouseEvent) => void;
}

function recruiterShareDisabledReason(kycStatus?: KycStatus): string | null {
  if (kycStatus === "rejected") {
    return IDENTITY_KYC_SHARE_BLOCK.rejected;
  }
  if (kycStatus !== "verified") {
    return IDENTITY_KYC_SHARE_BLOCK.unverified;
  }
  return null;
}

export function ShareIntakeLinkButton({
  existingLink,
  variant = "light",
  size = "md",
  fullWidth,
  label,
  onClick,
}: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isAffiliate = user?.role === "affiliate";
  const isRecruiter = user?.role === "recruiter";

  const profileQuery = useQuery({
    queryKey: ["my-profile"],
    queryFn: recruiterApi.myProfile,
    enabled: isRecruiter,
  });
  const affiliateQuery = useQuery({
    queryKey: ["affiliate-profile"],
    queryFn: affiliateApi.myProfile,
    enabled: isAffiliate,
  });
  const linksQuery = useQuery({
    queryKey: ["my-intake-links"],
    queryFn: intakeApi.listLinks,
    enabled: existingLink === undefined && (isRecruiter || isAffiliate),
  });

  const [shareOpen, setShareOpen] = useState(false);
  const [intakeLink, setIntakeLink] = useState<IntakeLink | null>(null);
  const [creatingLink, setCreatingLink] = useState(false);

  const knownLink =
    existingLink !== undefined
      ? existingLink
      : (linksQuery.data ?? []).find((link) => link.is_active) ?? null;

  const disabledReason = isAffiliate
    ? recruiterShareDisabledReason(affiliateQuery.data?.kyc_status)
    : recruiterShareDisabledReason(profileQuery.data?.kyc_status);
  const disabled = !!disabledReason;
  const buttonLabel = label ?? (knownLink ? "Show QR code" : "Share candidate link");

  async function handleShare(event: MouseEvent) {
    onClick?.(event);
    if (event.defaultPrevented) return;

    if (knownLink) {
      setIntakeLink(knownLink);
      setShareOpen(true);
      return;
    }

    setShareOpen(true);
    setCreatingLink(true);
    try {
      const link = await intakeApi.createLink();
      setIntakeLink(link);
      await queryClient.invalidateQueries({ queryKey: ["my-intake-links"] });
    } catch (err) {
      setShareOpen(false);
      const message = err instanceof ApiError ? err.message : "Could not create candidate link";
      notifications.show({ color: "red", title: "Share link failed", message });
    } finally {
      setCreatingLink(false);
    }
  }

  const button = (
    <Button
      variant={variant}
      color="violet"
      size={size}
      fullWidth={fullWidth}
      leftSection={knownLink ? <IconQrcode size="var(--axio-unit-16)" /> : <IconLink size="var(--axio-unit-16)" />}
      loading={creatingLink && !knownLink}
      disabled={disabled}
      onClick={handleShare}
    >
      {buttonLabel}
    </Button>
  );

  return (
    <>
      {disabled && disabledReason ? (
        <Tooltip label={disabledReason} multiline w="var(--axio-unit-280)">
          <span style={{ display: fullWidth ? "block" : "inline-block" }}>{button}</span>
        </Tooltip>
      ) : (
        button
      )}
      <ShareIntakeLinkModal
        opened={shareOpen}
        onClose={() => setShareOpen(false)}
        link={intakeLink}
        loading={creatingLink}
      />
    </>
  );
}
